import type { TargetAcknowledgement } from "@storyboard/dispatch-view/acknowledgement";
import { store } from "@/store/store";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { feedItemFold, feedWatermark, tmsEventArrivedAt, tmsEventFold } from "./acknowledgements";
import { type AssignmentFold, assignmentTargetKey, foldAssignments } from "./assignments";

/** The collections the fold reads: the handoffs, the three acknowledgement logs, and the targets' arrival instants. */
const ASSIGNMENT_COLLECTIONS = [
  "dispatchAssignment",
  "feedAcknowledgement",
  "feedItemAcknowledgement",
  "anchorAcknowledgement",
  "item",
  "tmsEvent",
] as const;

/**
 * The instant of the acknowledgement that stands on a target now, or `null`: its own newest standing acknowledgement,
 * or its channel's watermark when that covers the target's arrival and the target was not unacknowledged on its own
 * since. Whichever is later wins, so a driver row's `a` (a channel watermark) closes the handoff on its latest message.
 */
function acknowledgedAt(own: TargetAcknowledgement, arrivedAt: string | null, feedId: string | null): string | null {
  const ownAt = own.standing.at(-1)?.createdAt ?? null;
  const watermark = feedId ? feedWatermark(feedId) : null;
  const covered =
    watermark !== null &&
    arrivedAt !== null &&
    Date.parse(watermark.through) >= Date.parse(arrivedAt) &&
    !(own.lastUnacknowledgedAt !== null && Date.parse(own.lastUnacknowledgedAt) > Date.parse(watermark.createdAt));
  const watermarkAt = covered ? watermark.createdAt : null;
  if (ownAt === null) return watermarkAt;
  if (watermarkAt === null) return ownAt;
  return Date.parse(ownAt) >= Date.parse(watermarkAt) ? ownAt : watermarkAt;
}

/**
 * The reassignment prototype's open assignments in a workspace, recomputed as handoffs and acknowledgements land.
 *
 * @param workspaceId - Only this workspace's handoffs fold.
 * @param now - The clock the lookback is measured from, as epoch milliseconds; a minute clock keeps it moving.
 */
export function useAssignmentFold(workspaceId: string, now: number): AssignmentFold {
  return useCollectionsCompute(
    ASSIGNMENT_COLLECTIONS,
    () => {
      const rows = [...store.getAll("dispatchAssignment").values()].filter((row) => row.workspaceId === workspaceId);
      const feedOf = new Map(rows.map((row) => [assignmentTargetKey(row.targetKind, row.targetId), row.feedId]));
      return foldAssignments(
        rows,
        (targetKind, targetId) => {
          const feedId = feedOf.get(assignmentTargetKey(targetKind, targetId)) ?? null;
          if (targetKind === "feed_item") {
            const arrivedAt = store.get("item", targetId)?.createdAt ?? null;
            return acknowledgedAt(feedItemFold(targetId), arrivedAt, feedId);
          }
          const event = store.get("tmsEvent", targetId);
          if (!event) return null;
          return acknowledgedAt(tmsEventFold(event), tmsEventArrivedAt(event), feedId);
        },
        now,
      );
    },
    [workspaceId, now],
    "assignmentFold",
  );
}
